import React, { useState, useEffect } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  Container,
  Row,
  Col,
  Modal,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import FormInput from "../../../components/FormInput";
import {
  createInvoice,
  getAllInvoices,
  deleteInvoice,
  markSent,
  markPaid,
  markCancel,
  deleteSelectedInvoices,
} from "../../../api";

import BeatLoader from "react-spinners/BeatLoader";
import { useSnackbar } from "../../../utils/snackbar";

import { APIClient } from "../../../api/apiCore";
import moment from "moment";
import { createPortal } from "react-dom";

import { generateInvoicePDF } from "../../../api";
import LanguageSwitcher from "../../../components/LanguageSelector";
import { useTranslation } from "react-i18next";

const Invoices: React.FC = () => {
  interface RowData {
    id: number;
    invoice_number: string;
    client: string;
    total_amount: string;
    created_at: string;
    due_date: string;
    isChecked?: boolean; // Checkbox state for each row
  }

  interface OptionType {
    label: string;
    value: string;
  }

  interface InvoiceItem {
    id: number | string;
    item?: string;
    service_product_number?: string;
    description?: string;
    qty?: string;
    invoice_type?: string;
    unit_type?: string | number | null;
    unit_price?: string;
    selected_date?: Date | null;
    vat_type?: OptionType | null;
    total_cost_net?: string | number;
    total_cost_gross?: string | number;
    is_add_discount?: boolean;
    discount_type?: string;
    discount_price?: string | number;
    discount_percentage?: string | number;
    discounted_total_price_net?: string | number;
    discounted_total_price_gross?: string | number;
  }

  interface InvoiceData {
    id: string;
    status: number;
    items?: InvoiceItem[];
    client: { client_name: string | null; id: string | null };
    invoice_date: Date | null;
    due_date: Date | null | undefined;
    invoice_number: string;
    po_number: string;
    isChecked?: boolean;
  }

  const { t, i18n } = useTranslation();

  const showSnackbar = useSnackbar();
  const apiClient = new APIClient();
  const navigate = useNavigate();

  // state variables
  const [filterText, setFilterText] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);

  const [selectedValue, setSelectedValue] = useState<string>("");
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [data, setData] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [originalInvoices, setOriginalInvoices] = useState<InvoiceData[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const [invoiceDeleteFlag, setInvoiceDeleteFlag] = useState<any>("");

  const statusMap: Record<number, string> = {
    1: t("Draft"),
    2: t("Sent"),
    3: t("Paid"),
    4: t("Cancel"),
  };

  useEffect(() => {
    const fetchAllInvoices = async () => {
      try {
        setLoading(true);
        const response = await getAllInvoices();
        setInvoices(response.data);
        setOriginalInvoices(response.data);
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
      }
    };

    fetchAllInvoices();
  }, [invoiceDeleteFlag]);

  // Toggle all checkboxes
  const handleSelectAll = (isChecked: boolean) => {
    setSelectAll(isChecked);
    if (Array.isArray(invoices)) {
      const updatedData = invoices.map(row => ({ ...row, isChecked }));
      setInvoices(updatedData);
    }
  };

  //component functions
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleClose = (): void => setShowModal(false);
  const handleShow = (): void => setShowModal(true);

  // Function to toggle visibility
  const handleToggle = () => {
    setIsVisible(!isVisible);
  };

  const handleFilterTable = (inputValue: string) => {
    // Filter data based on the search text
    const filteredData = originalInvoices.filter(item =>
      item?.client?.client_name
        ?.toLowerCase()
        .includes(inputValue.toLowerCase()),
    );
    setFilterText(inputValue);
    setInvoices(filteredData);

    if (inputValue == "") {
      setData(originalInvoices);
    }
  };

  // Handle single checkbox toggle
  const handleCheckboxChange = (id: string, isChecked: boolean) => {
    const updatedData = invoices.map(row =>
      row.id == id ? { ...row, isChecked } : row,
    );

    setInvoices(updatedData);

    // If one is unchecked, uncheck "Select All"
    if (!isChecked) setSelectAll(false);
    else {
      // If all rows are selected, set "Select All" to true
      const allChecked = updatedData.every(row => row.isChecked);
      setSelectAll(allChecked);
    }
  };

  const handleGoToSubscriptionPage = () => {
    navigate("/subscription");
  };

  const handleGoToNewInvoicePage = () => {
    navigate("/new-invoice");
  };

  const handleGoToInvoiceEditPage = (id: string) => {
    navigate("/edit-invoice/" + id);
  };

  const handleViewPDF = async (id: string) => {
    navigate("/view-invoice-pdf/" + id);
  };

  const handleDownloadPDF = async (id: string, invoice_number: string) => {
    try {
      const response = (await apiClient.get(
        "/api/invoice/download-invoice-pdf",
        { invoice_id: id },
        "blob",
      )) as Blob;

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "invoice" + invoice_number + ".pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading invoice:", error);
    }
  };

  const handleDownloadEInvoice = async (id: string, invoice_number: string) => {
    try {
      const response = (await apiClient.get(
        "/api/invoice/download-invoice-xml",
        { invoice_id: id },
        "blob",
      )) as Blob;

      const blob = new Blob([response], { type: "application/xml" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = "invoice" + invoice_number + ".xml";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error: any) {}
  };

  const handleDeleteInvoice = async (id: string) => {
    try {
      const payload = {
        invoiceId: id,
      };

      const response = await deleteInvoice(payload);
      if (response.success == true) {
        showSnackbar(response.message, "info");
      } else {
        showSnackbar(response.message, "error");
      }
      setInvoiceDeleteFlag(new Date());
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const handleMarkSent = async (id: string) => {
    try {
      const payload = {
        invoiceId: id,
      };

      const response = await markSent(payload);
      if (response.success == true) {
        showSnackbar(response.message, "info");
      } else {
        showSnackbar(response.message, "error");
      }
      setInvoiceDeleteFlag(new Date());
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      const payload = {
        invoiceId: id,
      };

      const response = await markPaid(payload);
      if (response.success == true) {
        showSnackbar(response.message, "info");
      } else {
        showSnackbar(response.message, "error");
      }
      setInvoiceDeleteFlag(new Date());
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const handleMarkCancel = async (id: string) => {
    try {
      const payload = {
        invoiceId: id,
      };

      const response = await markCancel(payload);
      if (response.success == true) {
        showSnackbar(response.message, "info");
      } else {
        showSnackbar(response.message, "error");
      }
      setInvoiceDeleteFlag(new Date());
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const handleEmailInvoice = async (id: string) => {
    navigate("/email-invoice/" + id);
  };

  const handleDeleteSelectedInvoices = async () => {
    try {
      const selectedIds = invoices
        .filter(invoice => invoice.isChecked)
        .map(invoice => invoice.id);
      const response = await deleteSelectedInvoices(selectedIds);
      if (response.success == true) {
        setInvoiceDeleteFlag(new Date());
      }
    } catch (error: any) {}
  };

  // Custom styles for the table
  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "rgb(17, 125, 192)",
        color: "white",
        fontWeight: "500 !important",
        fontSize: ".975rem !important",
        // fontFamily: "Inter, sans-serif !important",
        letterSpacing: "0.5px",
      },
    },
    cells: {
      style: {
        padding: "10px 16px",
        fontSize: ".975rem",
        fontWeight: "400",
        color: "rgb(17 39 68)",
      },
    },
    pagination: {
      style: {
        fontSize: "14px",
        marginTop: "10px",
        justifyContent: "center",
        display: "flex",
      },
    },
  };

  // Define columns with the correct type
  const columns: TableColumn<(typeof invoices)[0]>[] = [
    {
      name: (
        <div>
          <input
            type="checkbox"
            checked={selectAll}
            onChange={e => handleSelectAll(e.target.checked)}
            title="Select All"
          />
        </div>
      ),
      cell: row => (
        <input
          type="checkbox"
          checked={!!row.isChecked}
          onChange={e => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
      width: "80px",
    },
    {
      name: t("Status"),
      selector: (row: InvoiceData) =>
        statusMap[row?.status as number] || "Unknown",
      sortable: true,
      cell: (row: InvoiceData) => {
        let backgroundColor;
        let color;

        // Define colors based on status
        switch (row?.status) {
          case 1:
            backgroundColor = "#f8d7da"; // Light red for "Draft"
            color = "#721c24";
            break;
          case 2:
            backgroundColor = "#fff3cd"; // Light yellow for "Pending"
            color = "#856404";
            break;
          case 3:
            backgroundColor = "#d4edda"; // Light green for "Paid"
            color = "#155724";
            break;
          default:
            backgroundColor = "#e2e3e5"; // Default gray background
            color = "#383d41";
        }

        return (
          <div
            style={{
              backgroundColor,
              color,
              padding: "5px 10px",
              borderRadius: "4px",
            }}
          >
            {statusMap[row?.status as number] || "Unknown"}
          </div>
        );
      },
    },
    {
      name: t("Number"),
      selector: row => row.invoice_number,
      sortable: true,
      width: "150px",
    },
    {
      name: t("Client"),
      selector: (row: InvoiceData) => row?.client?.client_name ?? "",
      sortable: true,
    },
    {
      name: t("Amount"),
      selector: (row: InvoiceData) =>
        (row?.items ?? [])
          .reduce((sum, item) => sum + Number(item?.total_cost_gross ?? 0), 0)
          .toFixed(2),
      sortable: true,
    },
    {
      name: t("Date"),
      selector: (row: InvoiceData) =>
        row?.invoice_date ? moment(row?.invoice_date).format("MM-DD-yyyy") : "",
      sortable: true,
    },
    {
      name: t("Due Date"),
      selector: (row: InvoiceData) =>
        row?.due_date ? moment(row?.due_date).format("MM-DD-yyyy") : "",
      sortable: true,
    },

    {
      name: t("Actions"),
      width: "150px",
      cell: row => (
        <>
          <Dropdown as={ButtonGroup}>
            <Dropdown.Toggle
              style={{
                backgroundColor: "rgb(17, 125, 192)",
                fontSize: "0.875rem",
                paddingLeft: "15px",
                paddingRight: "15px",
                fontWeight: "500",
              }}
              id="dropdown-basic"
            >
              {t("Actions")}
            </Dropdown.Toggle>
            {createPortal(
              <div style={{ position: "absolute", zIndex: 9999 }}>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => handleGoToInvoiceEditPage(row.id)}
                  >
                    <span>
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        font-size="18"
                        color="#117DC0"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: "rgb(17, 125, 192)" }}
                      >
                        <path fill="none" d="M0 0h24v24H0z"></path>
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
                      </svg>
                    </span>

                    <label className="default-text ms-2">{t("Edit")}</label>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() =>
                      handleDownloadPDF(row.id, row.invoice_number)
                    }
                  >
                    <span>
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        fontSize="18"
                        color="#117DC0"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: "rgb(17, 125, 192)" }}
                      >
                        <path fill="none" d="M0 0h24v24H0z"></path>
                        <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"></path>
                      </svg>
                    </span>

                    <label className="default-text ms-2">{t("Download")}</label>
                  </Dropdown.Item>

                  <Dropdown.Item
                    onClick={() =>
                      handleDownloadEInvoice(row.id, row.invoice_number)
                    }
                  >
                    <span>
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        fontSize="18"
                        color="#117DC0"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: "rgb(17, 125, 192)" }}
                      >
                        <path fill="none" d="M0 0h24v24H0z"></path>
                        <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z"></path>
                      </svg>{" "}
                    </span>

                    <label className="default-text ms-1">
                      {t("E-Invoice(XML)")}
                    </label>
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => handleMarkSent(row.id)}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      fontSize="18"
                      color="#117DC0"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: "rgb(17, 125, 192)" }}
                    >
                      <path fill="none" d="M0 0h24v24H0z"></path>
                      <path d="M12 19a6.995 6.995 0 0110-6.32V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h8.08c-.05-.33-.08-.66-.08-1zM4 6l8 5 8-5v2l-8 5-8-5V6zm13.34 16l-3.54-3.54 1.41-1.41 2.12 2.12 4.24-4.24L23 16.34 17.34 22z"></path>
                    </svg>

                    <label className="default-text ms-2">
                      {t("Mark Sent")}
                    </label>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleMarkPaid(row.id)}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      fontSize="18"
                      color="#117DC0"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: "rgb(17, 125, 192)" }}
                    >
                      <path fill="none" d="M0 0h24v24H0z"></path>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.88 15.76V19h-1.75v-1.29c-.74-.18-2.39-.77-3.02-2.96l1.65-.67c.06.22.58 2.09 2.4 2.09.93 0 1.98-.48 1.98-1.61 0-.96-.7-1.46-2.28-2.03-1.1-.39-3.35-1.03-3.35-3.31 0-.1.01-2.4 2.62-2.96V5h1.75v1.24c1.84.32 2.51 1.79 2.66 2.23l-1.58.67c-.11-.35-.59-1.34-1.9-1.34-.7 0-1.81.37-1.81 1.39 0 .95.86 1.31 2.64 1.9 2.4.83 3.01 2.05 3.01 3.45 0 2.63-2.5 3.13-3.02 3.22z"></path>
                    </svg>
                    <label className="default-text ms-2">
                      {t("Mark Paid")}
                    </label>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleMarkCancel(row.id)}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      fontSize="18"
                      color="#117DC0"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: "rgb(17, 125, 192)" }}
                    >
                      <path fill="none" d="M0 0h24v24H0z"></path>
                      <path d="M16.24 11.51l1.57-1.57-3.75-3.75-1.57 1.57-4.14-4.13c-.78-.78-2.05-.78-2.83 0l-1.9 1.9c-.78.78-.78 2.05 0 2.83l4.13 4.13L3 17.25V21h3.75l4.76-4.76 4.13 4.13c.95.95 2.23.6 2.83 0l1.9-1.9c.78-.78.78-2.05 0-2.83l-4.13-4.13zm-7.06-.44L5.04 6.94l1.89-1.9L8.2 6.31 7.02 7.5l1.41 1.41 1.19-1.19 1.45 1.45-1.89 1.9zm7.88 7.89l-4.13-4.13 1.9-1.9 1.45 1.45-1.19 1.19 1.41 1.41 1.19-1.19 1.27 1.27-1.9 1.9zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34c-.47-.47-1.12-.29-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
                    </svg>

                    <label className="default-text ms-2">
                      {t("Mark Cancel")}
                    </label>
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => handleViewPDF(row.id)}>
                    <span>
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        fontSize="18"
                        color="#117DC0"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: "rgb(17, 125, 192)" }}
                      >
                        <path fill="none" d="M0 0h24v24H0z"></path>
                        <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"></path>
                      </svg>
                    </span>

                    <label className="default-text ms-2">{t("View PDF")}</label>
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => handleEmailInvoice(row.id)}>
                    <span>
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        fontSize="18"
                        color="#117DC0"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: "rgb(17, 125, 192)" }}
                      >
                        <path fill="none" d="M0 0h24v24H0z"></path>
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                      </svg>
                    </span>

                    <label className="default-text ms-2">
                      {t("Email Invoice")}
                    </label>
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => handleDeleteInvoice(row.id)}>
                    <span>
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        fontSize="18"
                        color="#117DC0"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: "rgb(17, 125, 192)" }}
                      >
                        <path fill="none" d="M0 0h24v24H0z"></path>
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
                      </svg>
                    </span>

                    <label className="default-text ms-2">{t("Delete")}</label>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </div>,
              document.body,
            )}
          </Dropdown>
        </>
      ),
    },
  ];

  return (
    <div className="right-panel">
      <div className="sticky-top bg-white shadow-sm w-100 p-3 d-flex justify-content-between align-items-center z-index-100">
        <span className="mb-0 default-text" style={{ fontWeight: "500" }}>
          {t("E-Invoices")}
        </span>
        <span className="d-flex gap-3">
          <LanguageSwitcher />
          <Button variant="danger" onClick={handleGoToSubscriptionPage}>
            {t("Unlock Pro")}
          </Button>
        </span>
      </div>

      <div className="py-3  px-4">
        <div id="breadcrumb" className="d-flex gap-2 my-3">
          <i className="bx bx-home font-size-22"></i>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 h-5 w-5"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
          <span>
            <a style={{ fontWeight: "500" }}> {t("E-Invoices")}</a>
          </span>
        </div>
        {/* Search Input */}
        <div
          className="d-flex justify-content-between flex-column flex-md-row gap-3 pt-3"
          style={{ marginBottom: "10px" }}
        >
          <div>
            <Dropdown as={ButtonGroup}>
              <Dropdown.Toggle
                style={{
                  backgroundColor: "rgb(17, 125, 192)",
                  fontSize: "0.875rem",
                  paddingLeft: "15px",
                  paddingRight: "15px",
                  fontWeight: "500",
                }}
                id="dropdown-basic"
              >
                {t("Actions")}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={handleDeleteSelectedInvoices}>
                  <span>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      font-size="18"
                      color="#117DC0"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: "rgb(17, 125, 192)" }}
                    >
                      <path fill="none" d="M0 0h24v24H0z"></path>
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
                    </svg>
                  </span>

                  <label className="default-text ms-2">{t("Delete")}</label>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <div className="d-flex gap-2 w-auto ">
              <div className="input-group rounded-pill border bg-white">
                <span className="input-group-text bg-white border-0 rounded-pill">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  placeholder={t("Search...")}
                  className="form-control border-0 rounded-pill"
                  value={filterText}
                  onChange={e => handleFilterTable(e.target.value)}
                  style={{ outline: "none", boxShadow: "none" }}
                />
              </div>
              <div>
                <Button
                  variant="secondary"
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}
                  onClick={handleGoToNewInvoicePage}
                >
                  + {t("New Invoice")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "500px",
            }}
          >
            <BeatLoader color="gray" size={10} />
          </div>
        ) : (
          <DataTable
            title=""
            columns={columns}
            data={invoices}
            pagination
            sortIcon={
              <span
                style={{ color: "white", fontSize: "10px", marginLeft: "2px" }}
              >
                ▼
              </span>
            } // Always show sorting icon
            customStyles={customStyles} // Custom styles for header and pagination
            fixedHeader
            persistTableHead
            noDataComponent={
              // Display 'No results found' message when no data matches the search query
              <div
                style={{ textAlign: "center", padding: "20px", color: "gray" }}
              >
                {t("No results found")}
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default Invoices;
